import React, { useEffect, useState, createContext } from "react";
import adminsApi from "src/configs/apis/admins";
import adminRolesApi from "src/configs/apis/admin-roles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdminsPage from "./admins.page";
import { AdminProvider, useAdminContext } from "../../contexts/admin.context";
import AdminRoleDialog from "../../components/dialog/admin-role-dialog";
import AdminCompanyGroupDialog from "../../components/dialog/adminCompanyGroupDialog";
import ChangeAdminStatusDialog from "../../components/dialog/changeAdminStatusDialog";
import { addAlert } from "../../../notification";
import CreateAdminDialog from "../../components/dialog/createAdminDialog";
import createAdmin from "../../../../configs/apis/admins/createAdmin.api";
import EditAdminDialog from "../../components/dialog/editAdminDialog";
import AccessRights from "../../../../configs/constants/accessRights";

function Admins() {
  const [admins, setAdmins] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [itemPerPage, setItemPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [isStatusDialogOpen, setStatusDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canView = useSelector((state) => state.session.accessRights.includes(AccessRights.ADMIN_VIEW));
  const canEdit = useSelector((state) => state.session.accessRights.includes(AccessRights.ADMIN_EDIT));

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const {
    setActionOpen,
    setAdminRoleOpen,
    adminRoles,
    setAdminRoles,
    selectedAdmin,
  } = useAdminContext();
  const handleActionOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setActionOpen(true);
  };

  const fetch = () => {
    setLoading(true);
    adminsApi
      .getListing({ page: 0, length: itemPerPage, name: "", status: "" })
      .then((data) => {
        setAdmins(data.data.data);
        setTotal(data.data.recordsFiltered);
        setLoading(false);
      });
  };

  const getAdminRole = async () => {
    await adminRolesApi
      .getListing({ page: 0, length: itemPerPage, name: "", status: "" })
      .then((data) => {
        setAdminRoles(data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleAdminRoleOpen = async () => {
    getAdminRole();
    setAdminRoleOpen(true);
  };

  useEffect(() => {
    fetch();
  }, []);

  const handleGroupClick = async (value) => {
    setSelected(value);
    setOpenGroupDialog(true);
  };

  const handleChangeGroup = async (value) => {
    adminsApi.updateAdminCompanyGroup({ uuid: selected.uuid, groupUuid: value.group })
      .then(() => {
        setSelected(null);
        setOpenGroupDialog(false);
        dispatch(
          addAlert({
            severity: "success",
            message: "Admin company group updated successfully",
          })
        );
        fetch();
      });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleItemPerPageChange = (event) => {
    setItemPerPage(event.target.value);
    setPage(0);
  };

  const onCreateClick = () => {
    setCreateDialogOpen(true);
  };

  const onEditClick = (row) => {
    setSelected(row);
    setEditDialogOpen(true);
  };

  const onCreateClose = () => {
    setCreateDialogOpen(false);
  };

  const onEditClose = () => {
    setEditDialogOpen(false);
    setSelected(null);
  };

  const handleCreate = (value) => {
    createAdmin(value).then(() => {
      dispatch(
        addAlert({
          severity: "success",
          message: "Admin company group updated successfully",
        })
      );
      fetch();
      setCreateDialogOpen(false);
    });
  };

  const handleEdit = (value) => {
    adminsApi.updateAdmin({ uuid: selected.uuid, email: value.email, username: value.username }).then(() => {
      dispatch(
        addAlert({
          severity: "success",
          message: "Admin company group updated successfully",
        })
      );
      fetch();
      setEditDialogOpen(false);
    });
  };

  const onChangeStatusClick = (row) => {
    setStatusDialogOpen(true);
    setSelected(row);
  };

  const onCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelected(null);
  };

  const handleChangeStatus = () => {
    adminsApi.updateAdminStatus({ uuid: selected.uuid, isEnabled: selected.state !== 'active' }).then(() => {
      dispatch(
        addAlert({
          severity: "success",
          message: "Admin company status updated successfully",
        })
      );
      fetch();
      setStatusDialogOpen(false);
      setSelected(null);
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActionOpen(false);
  };

  return (
    <>
      <AdminsPage
        isCompaniesLoading={false}
        admins={admins}
        totalCompanies={1}
        companiesPage={1}
        page={page}
        handlePageChange={handlePageChange}
        itemPerPage={itemPerPage}
        handleItemPerPageChange={handleItemPerPageChange}
        allBranchWhiteLabelCount={1}
        handleActionOpen={handleActionOpen}
        handleAdminRoleOpen={handleAdminRoleOpen}
        anchorEl={anchorEl}
        isLoading={isLoading}
        handleGroupClick={handleGroupClick}
        total={total}
        onCreateClick={onCreateClick}
        onEditClick={onEditClick}
        onStatusChangeClick={onChangeStatusClick}
        canEdit={canEdit}
        handleClose={handleClose}
      />
      <CreateAdminDialog
        open={isCreateDialogOpen}
        handleClose={onCreateClose}
        handleSubmit={handleCreate}
      />
      <EditAdminDialog
        open={isEditDialogOpen && selected}
        handleClose={onEditClose}
        handleSubmit={handleEdit}
        selected={selected}
      />
      <ChangeAdminStatusDialog
        open={isStatusDialogOpen}
        handleClose={onCloseStatusDialog}
        handleSubmit={handleChangeStatus}
        isEnabled={selected?.state === 'active'}
      />
      <AdminRoleDialog onSuccess={fetch} />
      <AdminCompanyGroupDialog
        open={openGroupDialog}
        handleClose={() => {
          setSelected(null);
          setOpenGroupDialog(false);
        }}
        adminCompanyGroup={selected?.group_uuid}
        handleSubmit={handleChangeGroup}
      />
    </>
  );
}

function AdminsContainer() {
  return (
    <AdminProvider>
      <Admins />
    </AdminProvider>
  );
}

export default AdminsContainer;
